import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { productsAPI, ordersAPI, clientsAPI, settingsAPI } from '../services/api';
import { format, addDays, startOfDay, differenceInCalendarDays } from 'date-fns';
import { useDateLocale } from '../i18n/dateLocale';
import {
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import {
  ArrowForward as NextIcon,
  ArrowBack as BackIcon,
  Check as CheckIcon,
} from '@mui/icons-material';

const NewOrder = () => {
  const { user, hasRole } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dateLocale = useDateLocale();
  const [activeStep, setActiveStep] = useState(0);
  const [products, setProducts] = useState([]);
  const [productFilter, setProductFilter] = useState({ code: '', name: '' });
  const [clients, setClients] = useState([]);
  const [myClientGroup, setMyClientGroup] = useState(null); // fresh group for client role
  const [myDiscount, setMyDiscount] = useState(0); // fresh discount for client role
  const [orderingClosed, setOrderingClosed] = useState(false); // global kill-switch
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Map JS getDay() index → schedule key
  const DAY_MAP = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  // Returns the clientGroup for the currently relevant client (always fresh)
  const getClientGroup = (clientId, clientsList) => {
    if (user?.role === 'client') return myClientGroup;
    if (clientId && clientsList.length > 0) {
      return clientsList.find((c) => c.id === clientId)?.clientGroup ?? null;
    }
    return null;
  };

  const getMinAdvanceDays = (clientId, clientsList) => {
    const group = getClientGroup(clientId, clientsList);
    const val = group?.minOrderDaysAdvance;
    return val != null ? val : 2;
  };

  // Current weekday key + HH:MM in Argentina timezone
  const argentinaNow = () => {
    const now = new Date();
    const dayKey = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Argentina/Buenos_Aires', weekday: 'long',
    }).format(now).toLowerCase();
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Argentina/Buenos_Aires', hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(now);
    const h = String(parseInt(parts.find((p) => p.type === 'hour').value) % 24).padStart(2, '0');
    const m = parts.find((p) => p.type === 'minute').value.padStart(2, '0');
    return { dayKey, hhmm: `${h}:${m}` };
  };

  // Returns an i18n message if orders can't be placed right now per the group's
  // order-taking window (orderSchedule). Client role only — staff are never blocked.
  const getWindowBlockReason = (clientId, clientsList) => {
    if (user?.role !== 'client') return null;
    const os = getClientGroup(clientId, clientsList)?.orderSchedule;
    if (!os) return null;
    const { dayKey, hhmm } = argentinaNow();
    const day = os[dayKey];
    if (!day?.enabled) return t('orders.windowBlockedToday');
    const ranges = day.ranges || [];
    if (ranges.length === 0) return null; // enabled, no ranges → all-day
    const inWindow = ranges.some(({ start, end }) => hhmm >= start && hhmm <= end);
    if (!inWindow) {
      const rangeStr = ranges.map((r) => `${r.start}–${r.end}`).join(', ');
      return t('orders.windowBlocked', { ranges: rangeStr });
    }
    return null;
  };

  // Returns true if this date should be disabled in the picker
  const shouldDisableDate = (date, clientId, clientsList) => {
    const group = getClientGroup(clientId, clientsList);
    const minDays = group?.minOrderDaysAdvance ?? 2;
    const today = startOfDay(new Date());
    const diff = differenceInCalendarDays(startOfDay(date), today);

    if (minDays > 0 && diff < minDays) return true;

    if (group?.schedule) {
      const dayKey = DAY_MAP[date.getDay()];
      if (!group.schedule[dayKey]?.enabled) return true;
    }

    return false;
  };

  // Returns an i18n message explaining why the selected date is blocked (null = OK)
  const getDateBlockReason = (date, clientId, clientsList) => {
    if (!date) return null;
    const group = getClientGroup(clientId, clientsList);
    const minDays = group?.minOrderDaysAdvance ?? 2;
    const today = startOfDay(new Date());
    const diff = differenceInCalendarDays(startOfDay(date), today);

    if (minDays > 0 && diff < minDays) {
      return t('orders.dateBlockedAdvance', { count: minDays });
    }

    if (group?.schedule) {
      const dayKey = DAY_MAP[date.getDay()];
      if (!group.schedule[dayKey]?.enabled) {
        const activeDays = DAY_MAP.filter((k) => group.schedule[k]?.enabled);
        if (activeDays.length === 0) return t('orders.dateBlockedNoSchedule');
        const dayLabels = activeDays.map((k) => t(`clientGroups.days.${k}`)).join(', ');
        return t('orders.dateBlockedSchedule', { days: dayLabels });
      }
    }

    return null;
  };

  const [orderData, setOrderData] = useState({
    deliveryDate: addDays(new Date(), 2),
    clientId: user?.clientId || '',
    items: {},
    notes: '',
  });

  const steps = [t('orders.step1'), t('orders.step2'), t('orders.step3')];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [productsRes, clientsRes, meRes, orderingRes] = await Promise.all([
          productsAPI.getAll({ enabled: true }),
          hasRole(['admin', 'operator']) ? clientsAPI.getAll({ enabled: true }) : Promise.resolve({ data: [] }),
          user?.role === 'client' ? clientsAPI.getMe() : Promise.resolve({ data: null }),
          settingsAPI.get('ordering_enabled').catch(() => ({ data: { value: null } })),
        ]);
        setProducts(productsRes.data);
        setClients(clientsRes.data);
        setOrderingClosed(orderingRes.data?.value === 'false');
        const freshGroup = meRes.data?.clientGroup ?? null;
        setMyClientGroup(freshGroup);
        setMyDiscount(parseFloat(meRes.data?.discount) || 0);
        const initialItems = {};
        productsRes.data.forEach((p) => { initialItems[p.id] = 0; });
        setOrderData((prev) => {
          const minDays = freshGroup?.minOrderDaysAdvance ?? 2;
          return {
            ...prev,
            items: initialItems,
            deliveryDate: addDays(new Date(), minDays),
          };
        });
        setError('');
      } catch (err) {
        setError(t('orders.failedToLoad'));
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [hasRole]);

  const handleQuantityInput = (productId, value) => {
    const num = parseInt(value) || 0;
    setOrderData((prev) => ({
      ...prev,
      items: { ...prev.items, [productId]: Math.max(0, num) },
    }));
  };

  const getOrderItems = () =>
    Object.entries(orderData.items)
      .filter(([_, qty]) => qty > 0)
      .map(([productId, quantity]) => {
        const product = products.find((p) => p.id === productId);
        return { productId, quantity, price: product?.price || 0 };
      });

  const calculateTotal = () =>
    getOrderItems().reduce((sum, item) => {
      const product = products.find((p) => p.id === item.productId);
      return sum + item.quantity * parseFloat(product?.price || 0);
    }, 0);

  // Discount % for the client the order is being placed for
  const getDiscount = () => {
    if (user?.role === 'client') return myDiscount;
    const c = clients.find((cl) => cl.id === orderData.clientId);
    return parseFloat(c?.discount) || 0;
  };

  const calculateNet = () => calculateTotal() * (1 - getDiscount() / 100);

  const filteredProducts = products.filter((p) => {
    const codeMatch = (p.code || '').toLowerCase().includes(productFilter.code.trim().toLowerCase());
    const nameMatch = (p.name || '').toLowerCase().includes(productFilter.name.trim().toLowerCase());
    return codeMatch && nameMatch;
  });

  const handleNext = () => {
    if (activeStep === 0) {
      if (user?.role === 'client' && orderingClosed) {
        setError(t('orders.orderingClosed'));
        return;
      }
      if (hasRole(['admin', 'operator']) && !orderData.clientId) {
        setError(t('orders.selectClientFirst'));
        return;
      }
      const windowReason = getWindowBlockReason(orderData.clientId, clients);
      if (windowReason) {
        setError(windowReason);
        return;
      }
      const blockReason = getDateBlockReason(orderData.deliveryDate, orderData.clientId, clients);
      if (blockReason) {
        setError(blockReason);
        return;
      }
    }
    if (activeStep === 1 && getOrderItems().length === 0) {
      setError(t('orders.noProductsSelected'));
      return;
    }
    setError('');
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setError('');
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      await ordersAPI.create({
        deliveryDate: format(orderData.deliveryDate, 'yyyy-MM-dd'),
        clientId: orderData.clientId || user.clientId,
        items: getOrderItems(),
        notes: orderData.notes,
      });
      navigate('/orders');
    } catch (err) {
      setError(err.response?.data?.message || t('orders.failedToCreate'));
      setLoading(false);
    }
  };

  if (loading && activeStep === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>{t('orders.newOrder')}</Typography>

      {orderingClosed && user?.role === 'client' && (
        <Alert severity="warning" sx={{ mb: 2 }}>{t('orders.orderingClosed')}</Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>

      {/* Step 1: Delivery Date */}
      {activeStep === 0 && (() => {
        const minAdvanceDays = getMinAdvanceDays(orderData.clientId, clients);
        const clientGroup = getClientGroup(orderData.clientId, clients);
        const blockReason = getDateBlockReason(orderData.deliveryDate, orderData.clientId, clients);
        const windowReason = getWindowBlockReason(orderData.clientId, clients);
        const activeDays = clientGroup?.schedule
          ? DAY_MAP.filter((k) => clientGroup.schedule[k]?.enabled)
          : [];

        return (
          <Card>
            <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                {t('orders.step1')}
              </Typography>

              {windowReason && (
                <Alert severity="warning" sx={{ mb: 2 }}>{windowReason}</Alert>
              )}

              {hasRole(['admin', 'operator']) && (
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>{t('orders.client')}</InputLabel>
                  <Select
                    value={orderData.clientId}
                    onChange={(e) => {
                      const newClientId = e.target.value;
                      const newMinDays = getMinAdvanceDays(newClientId, clients);
                      setOrderData({
                        ...orderData,
                        clientId: newClientId,
                        deliveryDate: addDays(new Date(), newMinDays),
                      });
                    }}
                    label={t('orders.client')} required>
                    {clients.map((client) => (
                      <MenuItem key={client.id} value={client.id}>
                        {client.companyName} ({client.clientNumber})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <DatePicker
                label={t('orders.deliveryDate')}
                value={orderData.deliveryDate}
                onChange={(date) => { setOrderData({ ...orderData, deliveryDate: date }); setError(''); }}
                shouldDisableDate={(date) => shouldDisableDate(date, orderData.clientId, clients)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    error: !!blockReason,
                    helperText: blockReason || undefined,
                  },
                }}
              />

              {/* Available days summary */}
              {activeDays.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                    {t('orders.availableDays')}:
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                    {DAY_MAP.map((k) => {
                      const enabled = activeDays.includes(k);
                      return (
                        <Chip
                          key={k}
                          label={t(`clientGroups.days.${k}`).substring(0, 3)}
                          size="small"
                          color={enabled ? 'primary' : 'default'}
                          variant={enabled ? 'filled' : 'outlined'}
                          sx={{ opacity: enabled ? 1 : 0.35 }}
                        />
                      );
                    })}
                  </Stack>
                </Box>
              )}

              {/* Advance-days hint */}
              {minAdvanceDays > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {t('orders.deliveryDateHintDays', { count: minAdvanceDays })}
                </Typography>
              )}
              {minAdvanceDays === 0 && !clientGroup && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {t('orders.deliveryDateHintNoRestriction')}
                </Typography>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* Step 2: Products */}
      {activeStep === 1 && (
        <>
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Button variant="outlined" onClick={handleBack} startIcon={<BackIcon />}>
              {t('common.back')}
            </Button>
            <Button variant="contained" onClick={handleNext} endIcon={<NextIcon />}>
              {t('common.next')}
            </Button>
          </Box>

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                {t('orders.step2')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('orders.deliveryDate')}: {format(orderData.deliveryDate, 'PPPP', { locale: dateLocale })}
              </Typography>
            </CardContent>
          </Card>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 600 }}>{t('orders.productCode')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('orders.product')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('orders.description')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('orders.price')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('orders.quantity')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">{t('orders.subtotal')}</TableCell>
                </TableRow>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ pt: 0 }}>
                    <TextField
                      fullWidth size="small" variant="standard" placeholder={t('orders.filterByCode')}
                      value={productFilter.code}
                      onChange={(e) => setProductFilter({ ...productFilter, code: e.target.value })}
                    />
                  </TableCell>
                  <TableCell sx={{ pt: 0 }}>
                    <TextField
                      fullWidth size="small" variant="standard" placeholder={t('orders.filterByName')}
                      value={productFilter.name}
                      onChange={(e) => setProductFilter({ ...productFilter, name: e.target.value })}
                    />
                  </TableCell>
                  <TableCell colSpan={4} />
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProducts.map((product) => {
                  const quantity = orderData.items[product.id] || 0;
                  const price = parseFloat(product.price) || 0;
                  return (
                    <TableRow key={product.id}>
                      <TableCell><code>{product.code}</code></TableCell>
                      <TableCell>{product.name}</TableCell>
                      <TableCell>{product.description}</TableCell>
                      <TableCell>${price.toFixed(2)}</TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          value={quantity}
                          onChange={(e) => handleQuantityInput(product.id, e.target.value)}
                          inputProps={{ min: 0, style: { textAlign: 'center', width: 80 } }}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">${(quantity * price).toFixed(2)}</TableCell>
                    </TableRow>
                  );
                })}
                {filteredProducts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                      {t('orders.noProductsMatch')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Card sx={{ mt: 2, bgcolor: 'primary.main', color: 'white' }}>
            <CardContent>
              {getDiscount() > 0 ? (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: 0.85 }}>
                    <Typography variant="body1">{t('orders.totalAmount')}:</Typography>
                    <Typography variant="body1" sx={{ textDecoration: 'line-through' }}>${calculateTotal().toFixed(2)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: 0.85 }}>
                    <Typography variant="body2">{t('orders.discount')} ({getDiscount()}%):</Typography>
                    <Typography variant="body2">−${(calculateTotal() - calculateNet()).toFixed(2)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>{t('orders.netAmount')}:</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>${calculateNet().toFixed(2)}</Typography>
                  </Box>
                </>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{t('orders.totalAmount')}:</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>${calculateTotal().toFixed(2)}</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Step 3: Review */}
      {activeStep === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>{t('orders.step3')}</Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary">{t('orders.deliveryDate')}</Typography>
              <Typography variant="body1">{format(orderData.deliveryDate, 'PPPP', { locale: dateLocale })}</Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
              {t('orders.orderItems')}
            </Typography>

            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>{t('orders.product')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">{t('orders.qty')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">{t('orders.price')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">{t('orders.subtotal')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getOrderItems().map((item) => {
                    const product = products.find((p) => p.id === item.productId);
                    return (
                      <TableRow key={item.productId}>
                        <TableCell>{product?.name}</TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">${parseFloat(item.price).toFixed(2)}</TableCell>
                        <TableCell align="right">
                          ${(item.quantity * parseFloat(item.price)).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell colSpan={3} align="right" sx={{ fontWeight: 600 }}>
                      {t('orders.totalLabel')}:
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      ${calculateTotal().toFixed(2)}
                    </TableCell>
                  </TableRow>
                  {getDiscount() > 0 && (
                    <>
                      <TableRow>
                        <TableCell colSpan={3} align="right" sx={{ color: 'success.main' }}>
                          {t('orders.discount')} ({getDiscount()}%):
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'success.main' }}>
                          −${(calculateTotal() - calculateNet()).toFixed(2)}
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell colSpan={3} align="right" sx={{ fontWeight: 700 }}>
                          {t('orders.netAmount')}:
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          ${calculateNet().toFixed(2)}
                        </TableCell>
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TextField
              fullWidth
              label={t('orders.notes')}
              multiline rows={3}
              value={orderData.notes}
              onChange={(e) => setOrderData({ ...orderData, notes: e.target.value })}
              placeholder={t('orders.notesPlaceholder')}
            />
          </CardContent>
        </Card>
      )}

      {/* Navigation */}
      <Box display="flex" justifyContent="space-between" mt={3}>
        <Button variant="outlined" onClick={handleBack} disabled={activeStep === 0} startIcon={<BackIcon />}>
          {t('common.back')}
        </Button>

        {activeStep === steps.length - 1 ? (
          <Button variant="contained" onClick={handleSubmit} disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}>
            {loading ? t('orders.placingOrder') : t('orders.placeOrder')}
          </Button>
        ) : (
          <Button variant="contained" onClick={handleNext} endIcon={<NextIcon />}>
            {t('common.next')}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default NewOrder;
